<template>
  <div v-if="applicantData" class="no-overflow-modal">
    <div class="columns">
      <div class="column b-r">
        <div dir="ltr">
          <!-- blocked candidate (v2): banner + manager-only release -->
          <ApplicantBlockBanner :applicant="applicantData" @released="onBlockReleased" />
          <ApplicantControlModule :applicantData="applicantData" />
          <!-- R1.3 - submission consent evidence (click, read-only) -->
          <div class="has-text-right my-2">
            <SubmissionConsentButton :applicantId="applicantData.resource_id" :summary="applicantData.submission_consent_summary || null" />
            <button
              v-if="!applicantData.blocked"
              class="button is-small is-danger is-outlined mr-2"
              :class="{ 'is-loading': isBlocking }"
              data-test="block-applicant-button"
              @click="blockApplicant"
            >חסימת מועמד/ת</button>
          </div>
          <!-- abstract -->
          <div>
            <Abstract
              :applicantId="applicantData.resource_id"
              v-model="abstract"
              :isEditable="false"
              :updatedAt="abstractUpdatedAt"
              :updatedBy="abstractUpdatedBy"
              :updatedByLabel="abstractUpdatedByLabel"
              :chatbotAbstract="chatbotAbstract"
              :chatbotHandoffSummary="chatbotHandoffSummary"
              :chatbotAbstractGeneratedAt="chatbotAbstractGeneratedAt"
            />
          </div>
          <!-- message -->
          <div class="has-text-left mt-2">
            <Message :userId="userId" :applicantId="applicantId" v-model="message" />
          </div>
          <!-- job_titles -->
          <div class="mt-3">
            <ApplicantJobTitles v-if="applicantData" :applicant="applicantData" />
          </div>
          <div class="box py-2 mt-3">
            <!-- job description -->
            <div class="has-text-left">
              <ResourceTextEditor :payload="{ text_data: texts.job_description, designator: 'job_description', resource_type: 'Applicant', resource_id: applicantData.resource_id }" />
            </div>
            <!-- Salary Expectations: summary (R1.4) -->
            <p class="mt-3 is-size-7 has-text-grey" dir="rtl" data-test="salary-summary">
              {{ salarySummary }}<span v-if="salaryRespondedLabel"> · {{ salaryRespondedLabel }}</span>
            </p>
            <!-- Salary Expectations min + חשבונית  -->
            <div class="mt-2 has-text-left">
              <SalaryExpectations field="min" :applicantId="applicantData.resource_id" v-model="minSalaryExpectations" />
            </div>
            <!-- Salary Expectations max -->
            <div class="mt-3 has-text-left">
              <SalaryExpectations field="max" :applicantId="applicantData.resource_id" v-model="maxSalaryExpectations" />
            </div>
            <!-- Salary Expectations comment -->
            <div class="mt-3 has-text-left">
              <SalaryExpectationsComment :applicantId="applicantData.resource_id" v-model="salaryExpectationsComment" />
            </div>
            <!-- is discrete -->
            <div class="mt-3">
              <IsDiscrete :applicantId="applicantData.resource_id" v-model="isDiscrete" />
            </div>
            <!-- current company -->
            <div class="mt-3 has-text-left">
              <CurrentCompany :applicantId="applicantData.resource_id" v-model="currentCompany" />
            </div>
            <!-- no-go companies -->
            <div class="mt-3 has-text-left">
              <NoGoCompanies :applicantId="applicantData.resource_id" v-model="noGoCompanies" />
            </div>
            <!-- free submition -->
            <div class="mt-3">
              <FreeSubmission :applicantId="applicantData.resource_id" v-model="freeSubmission" :approvedAt="freeSubmissionApprovedAt" @update:approvedAt="freeSubmissionApprovedAt = $event" />
            </div>
            <div class="mt-3 has-text-left">
              <p><span class="has-text-weight-bold">Applicant code:</span> {{ this.$utils.getPositionApplicantCode(applicantData.resource_id) }}</p>
            </div>
            <div class="columns mt-2">
              <div class="column">
                <!-- status -->
                <div class="field">
                    <Status :applicantId="applicantData.resource_id" v-model="applicantStatus" />
                </div>
                <!-- location -->
                <div class="has-text-left">
                  <LocationEditor :payload="{ location, resource_type: 'Applicant', resource_id: applicantData.resource_id }" />
                </div>
              </div>
              <div class="column"></div>
            </div>
          </div>

          <!-- conversation facts from bot -->
          <ConversationFacts :applicant_id="applicantData.resource_id" />

          <!-- skills display (same as current on applicant modal) -->
          <div class="box" v-if="Object.keys(skills_by_category).length > 0">
            <p><b><u>סקילס:</u></b></p>
            <div :key="category" v-for="(skill, category) in skills_by_category">
              <div>
                <span><b>{{ category }}: </b></span>
                <span :key="skill.id" v-for="skill in skills_by_category[category]"
                  >{{ skill.skill.name }}<span v-if="skill.applicant_comment" class="has-text-grey-light"> ({{ skill.applicant_comment }})</span> | </span
                >
              </div>
            </div>
          </div>

          <!-- VA2 - chatbot skill answers (yes/no + date, expired greyed) -->
          <SkillAnswers :skillAnswers="applicantData.skill_answers || []" />

          <div class="box">
            <!-- work on friday skill -->
            <!-- work on friday -->
            <table class="table">
              <tbody>
                <tr v-for="skill in specialSkills" :key="skill.id">
                  <td class="bold">{{ skill.question_text }}</td>
                  <td style="width: 40px">
                    <button class="button" :class="{'is-success': skill.has_experience === true}" @click="answerSkillQuestion(skill, true)">Yes</button>
                  </td>
                  <td style="width: 40px">
                    <button class="button" :class="{'is-danger': skill.has_experience === false}" @click="answerSkillQuestion(skill, false)">No</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <!-- 11 months alert -->
            <div class="has-text-left">
              <button class="button is-warning" @click="createElevenMonthAlert">Create 11 months alert</button>
            </div>
          </div>

          <!-- internal notes -->
          <div class="has-text-left mt-4">
            <p><b>Internal Notes:</b></p>
            <InternalNotes :payload="applicantData" :is_modal="false" />
          </div>

          <div class="mt-3">
             <!-- job history (single simple from new vetting modal) -->
             <ApplicantJobHistory v-if="applicantData" :applicant="applicantData" />
           </div>
          <!-- VA2 - work history collected by the chatbot (read-only) -->
          <ChatWorkHistory class="mt-3" :workHistory="applicantData.work_history || []" />

          <div class="box mt-4">
            <div class="is-flex is-justify-content-space-between">
              <div class="has-text-left is-flex-grow-1">
                <!-- Replace static phone number with component -->
                <div class="mt-2 has-text-left">
                  <PhoneNumber :userId="userId" v-model="applicantPhone" />
                </div>
              </div>
            </div>
            <div class="has-text-left mt-2">
              <Email :userId="userId" v-model="applicantEmail" />
            </div>
            <!-- gender component with its own styling -->
            <div class="mt-2 has-text-left">
              <Gender :applicantId="applicantData.resource_id" v-model="applicantGender" />
            </div>
            <!-- preferred work region -->
            <div class="mt-2 has-text-left">
              <PreferredWorkRegion :applicantId="applicantData.resource_id" v-model="preferredWorkRegion" />
              <PreferredRegionSummary
                :region="preferredWorkRegion"
                :cities="applicantData.preferred_cities || []"
                :regionUpdatedAt="applicantData.preferred_work_region_updated_at || null"
                :regionUpdatedBy="applicantData.preferred_work_region_updated_by || null"
                :citiesUpdatedAt="applicantData.preferred_cities_updated_at || null"
                :citiesUpdatedBy="applicantData.preferred_cities_updated_by || null"
              />
            </div>
            <div class="mt-2 has-text-left">
              <RestrictWhatsappDate :applicantId="applicantData.resource_id" v-model="restrictWhatsappDate" />
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <!-- applicant_id -->
              <div class="field mt-5 pl-1">
                <p class="columns has-text-left">
                  <b class="column">Applicant ID: </b>
                  <span class="column">
                    {{applicantData.resource_id}}
                    <button
                      type="button"
                      class="button is-small is-light ml-2 copy-applicant-id"
                      title="העתק מזהה מועמד"
                      aria-label="העתק מזהה מועמד"
                      @click="copyApplicantId"
                    >
                      <span class="icon is-small"><i class="fas fa-copy"></i></span>
                    </button>
                  </span>
                </p>
              </div>
            </div>
            <div class="column">
              <!-- owner -->
              <div class="field mt-4">
                <Owner :applicantId="applicantData.resource_id" v-model="applicantOwner" />
              </div>
            </div>
          </div>

          <div class="box">
            <button class="button is-info mt-5 mb-4 ml-3" @click="getRawTextOfApplicant">Get Raw Text</button>
            <textarea v-if="rawText" style="display: block; width: 100%;" cols="30" rows="10" placeholder="..." :value="rawText"></textarea>
          </div>

        </div>
      </div>
      <div v-if="showPdf" class="column column-width is-flex is-flex-direction-column" style="overflow: hidden;">
        <div class="box p-1 applicant_cv_pdf is-flex-grow-1" style="overflow: hidden;">
          <ApplicantCvPdf :payload="applicantData" containerStyle="profile-container" class="full_height applicant_cv_pdf applicant_profile_html_container" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapMutations } from 'vuex'

import TextsHelper from '@/custom/classes/texts_helper.js'
import SkillsHelper from '@/custom/classes/skills_helper.js'
import InternalNotes from '@/components/InternalNotes.vue'
import ResourceTextEditor from '@/components/ResourceTextEditor.vue'
import LocationEditor from '@/components/LocationEditor.vue'
import ApplicantJobTitles from '@/components/entities/applicant/JobTitles'
import ApplicantJobHistory from '@/components/ApplicantJobHistory'
import FreeSubmission from '@/components/entities/applicant/FreeSubmission'
import IsDiscrete from '@/components/entities/applicant/IsDiscrete'
import SalaryExpectationsComment from '@/components/entities/applicant/SalaryExpectationsComment'
import { formatDate } from '@/custom/helpers/format_date'
import SalaryExpectations from '@/components/entities/applicant/SalaryExpectations.vue'
import PreferredWorkRegion from '@/components/entities/applicant/PreferredWorkRegion'
import Status from '@/components/entities/applicant/Status'
import Owner from '@/components/entities/applicant/Owner'
import Email from '@/components/entities/applicant/Email'
import Abstract from '@/components/entities/applicant/Abstract'
import ApplicantCvPdf from '@/components/ApplicantCvPdf.vue'
import ApplicantControlModule from '@/components/shared/organisms/ApplicantControlModule.vue'
import Message from '@/components/entities/applicant/Message'
import RestrictWhatsappDate from '@/components/entities/applicant/RestrictWhatsappDate'
import Gender from '@/components/entities/applicant/Gender'
import PhoneNumber from '@/components/entities/applicant/PhoneNumber'
import CurrentCompany from '@/components/entities/applicant/CurrentCompany'
import NoGoCompanies from '@/components/entities/applicant/NoGoCompanies'
import ConversationFacts from '@/components/applicants/ConversationFacts.vue'
import SubmissionConsentButton from '@/components/SubmissionConsentButton.vue'
import ApplicantBlockBanner from '@/components/ApplicantBlockBanner.vue'
import { blockApplicantFlow, markReleased } from '@/custom/helpers/applicant_block.js'
import ChatWorkHistory from '@/components/entities/applicant/ChatWorkHistory.vue'
import PreferredRegionSummary from '@/components/entities/applicant/PreferredRegionSummary.vue'
import SkillAnswers from '@/components/entities/applicant/SkillAnswers.vue'

export default {
  name: 'ModalApplicant',
  components: {
    ApplicantCvPdf,
    InternalNotes,
    ResourceTextEditor,
    ApplicantJobTitles,
    ApplicantJobHistory,
    LocationEditor,
    FreeSubmission,
    IsDiscrete,
    SalaryExpectations,
    PreferredWorkRegion,
    PreferredRegionSummary,
    ChatWorkHistory,
    SkillAnswers,
    RestrictWhatsappDate,
    SalaryExpectationsComment,
    Status,
    Abstract,
    Owner,
    Email,
    Message,
    ApplicantControlModule,
    Gender,
    PhoneNumber,
    CurrentCompany,
    NoGoCompanies,
    ConversationFacts,
    SubmissionConsentButton,
    ApplicantBlockBanner
  },
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isBlocking: false,
      texts_helper: new TextsHelper(),
      skills_helper: new SkillsHelper(),
      applicantName: this.payload.data.first_name + ' ' + this.payload.data.last_name,
      location: {},
      freeSubmission: null,
      freeSubmissionApprovedAt: null,
      texts: {},
      skills_by_category: {},
      showPdf: this.payload.modal_open_action == 'click' || this.payload.modal_open_action == undefined,
      isDiscrete: false,
      rawText: null,
      salaryExpectationsComment: '',
      minSalaryExpectations: '',
      maxSalaryExpectations: '',
      salaryRespondedAt: null,
      preferredWorkRegion: '',
      applicantStatus: null,
      applicantOwner: null,
      applicantData: undefined,
      abstract: undefined,
      // N10 - abstract provenance, served by Admin::V1::ApplicantSerializer.
      // abstractUpdatedAt was declared here long before anything assigned it;
      // init() now fills both from the applicant payload.
      abstractUpdatedAt: null,
      abstractUpdatedBy: null,
      abstractUpdatedByLabel: null,
      chatbotAbstract: null,
      chatbotHandoffSummary: null,
      chatbotAbstractGeneratedAt: null,
      restrictWhatsappDate: '',
      specialSkills: {},
      message: '',
      messageLengthLimit: 500,
      historyCompanies: [],
      applicantPhone: '',
      applicantEmail: '',
      applicantGender: '',
      currentCompany: '',
      noGoCompanies: ''
    }
  },
  created () {
    this.init()
  },
  computed: {
    // R1.4 - "מינימום X · מקסימום Y"; '—' for a missing side.
    salarySummary () {
      const fmt = v => {
        const n = Number(v)
        return v === '' || v === null || v === undefined || isNaN(n) || n === 0 ? '—' : Math.round(n).toLocaleString('en-US')
      }
      return `מינימום ${fmt(this.minSalaryExpectations)} · מקסימום ${fmt(this.maxSalaryExpectations)}`
    },
    salaryRespondedLabel () {
      const d = formatDate(this.salaryRespondedAt)
      return d ? `עודכן ${d}` : ''
    },
    userId () {
      if (!this.applicantData) return null
      return this.applicantData.user_id != null ? this.applicantData.user_id : this.applicantData.id
    },
    applicantId () {
      if (!this.applicantData) return null
      return this.applicantData.applicant_id != null ? this.applicantData.applicant_id : this.applicantData.resource_id
    }
  },
  methods: {
    // Copy the applicant id (resource_id) shown in the card. Falls back to the
    // execCommand helper when the async clipboard API is unavailable (non-secure
    // context / older browser) or rejects.
    async copyApplicantId () {
      const text = String(this.applicantData.resource_id)
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          this.$utils.copyToClipboard(text)
        }
      } catch (err) {
        try {
          this.$utils.copyToClipboard(text)
        } catch (fallbackErr) {
          this.$utils.emit('show_toast', { text: 'שגיאה בהעתקה', type: 'error' })
          return
        }
      }
      this.$utils.emit('show_toast', { text: 'הועתק', type: 'success', duration: 2000 })
    },
    onBlockReleased () {
      markReleased(this, this.applicantData)
    },
    async blockApplicant () {
      if (this.isBlocking) return
      this.isBlocking = true
      try {
        const res = await blockApplicantFlow(this, this.applicantData.resource_id)
        if (res) {
          // Re-read the applicant so banner/disabled controls reflect the server's block json.
          const fresh = res.applicant && res.applicant.block ? res.applicant : null
          this.$set(this.applicantData, 'blocked', true)
          this.$set(this.applicantData, 'block', fresh ? fresh.block : null)
        }
      } finally {
        this.isBlocking = false
      }
    },
    async init () {
      const data = await this.$network_utils.getApplicant({ id: this.payload.data.resource_id })
      this.applicantData = data
      this.freeSubmission = data.free_submission
      this.freeSubmissionApprovedAt = data.free_submission_approved_at || null
      this.isDiscrete = data.is_discrete
      this.applicantStatus = data.applicant_status
      this.applicantOwner = data.owner
      this.salaryExpectationsComment = data.salary_expectations_comment || ''
      this.minSalaryExpectations = data.min_salary_expectations || ''
      this.maxSalaryExpectations = data.max_salary_expectations || ''
      this.salaryRespondedAt = data.salary_responded_at || null
      this.preferredWorkRegion = data.preferred_work_region || ''
      this.message = data.message || ''
      this.applicantPhone = data.phone_number
      this.applicantEmail = data.email
      this.applicantGender = data.gender
      this.restrictWhatsappDate = data.restrict_whatsapp_date || ''
      this.currentCompany = data.current_company || ''
      this.noGoCompanies = data.no_go_companies || ''
      this.abstract = data.abstract ? data.abstract.replace(/(\\n|\\n\.|\/n|\/n\.)/g, '\n') : ''
      this.abstractUpdatedAt = data.abstract_updated_at || null
      this.abstractUpdatedBy = data.abstract_updated_by || null
      this.abstractUpdatedByLabel = data.abstract_updated_by_label || null
      this.chatbotAbstract = data.chatbot_abstract || null
      this.chatbotHandoffSummary = data.chatbot_handoff_summary || null
      this.chatbotAbstractGeneratedAt = data.chatbot_abstract_generated_at || null
      this.getSpecialSkills()
      this.getLocation()
      this.getTexts()
      this.getHistoryCompanies()
    },
    getBoldAbstract () {
      return this.texts_helper.getBoldContent(this.abstract, 'abstract')
    },
    async submitAbstract () {
      await this.$network_utils.updateApplicant({ applicant_id: this.applicantData.resource_id, abstract: this.getBoldAbstract() })
      this.$utils.emit('show_toast', { text: 'מעדכן אבסטרקט' })
    },
    async getTexts () {
      const response = await this.$network_utils.getResourceTexts({ current_type: 'Applicant', current_resource_id: this.applicantData.resource_id })
      this.setTexts(response)
    },
    setTexts (server_response) {
      let temp_texts = {}
      for (let i = 0; i < server_response.length; i++) {
        let text = server_response[i]
        let designator = text.designator
        text.content = this.texts_helper.getContent(text)
        temp_texts[designator] = text
      }
      this.texts = temp_texts
    },
    async handleFocusOutMessage () {
      await this.$caller.request('put', `/users/${this.userId}`, 'admin_headers', { message: this.message })
    },
    async createElevenMonthAlert () {
      await this.$network_utils.postAlert({
        designator: 'missions',
        content: 'ליצור קשר עוד 11 חודשים',
        status: 'active',
        priority: 90,
        resource_type: 'Applicant',
        resource_id: this.applicantData.resource_id,
        timing: this.$moment().add(11, 'months')
      })
      this.$utils.emit('show_toast', { text: 'נוצר בהצלחה' })
    },
    openWhatsappModal () {
      this.create({
        designator: 'applicant-whatsapp-modal',
        component_name: 'ModalWhatsapp',
        modal_title: this.applicantData.first_name + ' ' + this.applicantData.last_name + ' - מועמד',
        modal_width: 500,
        is_center_modal: true,
        props: {
          applicant: this.applicantData
        }
      })
    },
    openFollowUp () {
      this.create({
        designator: 'resource-alerts-form-modal',
        component_name: 'ApplicantAlertsForm',
        modal_width: 2000,
        is_center_modal: true,
        data: this.applicantData,
        resource_type: 'Applicant',
        modal_title: `Alerts - ${this.$utils.getResourceFullName({ resource_type: 'Applicant', data: this.applicantData })} ${this.applicantData.phone_number}`
      })
    },
    prettifyDate (date) {
      const a = date.split('/')
      return `${a[1]}-${a[0].padStart(2, '0')}-01`
    },
    async getRawTextOfApplicant () {
      const response = await this.$network_utils.getApplicantRawText({ id: this.applicantData.resource_id })
      this.rawText = response.data
    },
    getLocation () {
      this.$network_utils.getResourceLocations({ current_type: 'Applicant', current_resource_id: this.applicantData.resource_id }).then(response => {
        this.location = response[0]
      })
    },
    async applicantAnswered () {
      await this.$network_utils.postStat({
        title: 'Applicant answered phone',
        designator: 'applicant_answered_phone',
        status: 'active',
        owner: this.$utils.getOwner().name,
        priority: 5,
        resource_type: 'Applicant',
        resource_id: this.applicantData.resource_id
      })
      this.$utils.emit('show_toast', { text: 'success' })
    },
    async getHistoryCompanies () {
      this.historyCompanies = await this.$network_utils.getApplicantHistoryCompanies({ current_type: 'Applicant', current_applicant_id: this.applicantData.resource_id })
    },
    async getSpecialSkills () {
      const skillsName = ['עבודה בימי שישי', 'עבודה בימי שישי לסירוגין', 'משרה מלאה', 'עבודה בלילה']
      for (let i = 0; i < skillsName.length; i++) {
        const skill = skillsName[i]
        const [ result ] = await this.$network_utils.getAllSkills({ name_eq: skill })
        if (result) {
          this.specialSkills[result.id] = result
        }
      }
      this.getSkills()
    },
    async answerSkillQuestion (skill, answer) {
      const response = await this.$network_utils.postApplicantSkill({ skill_id: skill.id, current_applicant_id: this.applicantData.resource_id, has_experience: answer })
      skill.has_experience = answer
      this.specialSkills = { ...this.specialSkills, [response.skill.id]: { ...skill } }
    },
    getSkills () {
      this.$network_utils
        .getApplicantSkills({
          current_applicant_id: this.applicantData.resource_id
        })
        .then(response => {
          response.forEach(s => {
            const skill = this.specialSkills[s.skill.id]
            if (skill) {
              skill.has_experience = s.details.has_experience
            }
          })
          this.skills_by_category = this.skills_helper.getSkillsByCategory(
            response
          )
        })
    },
    openFilesModal () {
      this.create({ designator: 'applicant-files-modal', component_name: 'Files', applicant_data: this.applicantData, applicant_id: this.applicantData.resource_id, modal_title: this.applicantName + ' Files' })
    },
    openSkillsModal () {
      this.create({ designator: 'applicant-skill-form-modal', component_name: 'ApplicantSkillsForm', data: this.applicantData, modal_title: this.applicantName + ' Skills', modal_width: 1300, is_center_modal: true })
    },
    openNewVettingModal () {
      let modal_title = this.applicantData.first_name + ' ' + this.applicantData.last_name + ' Vetting'
      this.create({ designator: 'vetting-modal', component_name: 'ModalVettingForm', applicant_id: this.applicantData.resource_id, modal_title: modal_title, ignore_escape_button: true, modal_width: 2400, is_center_modal: true })
    },
    openWhatsappConversation () {
      this.create({
        designator: 'applicant-whatsapp-modal',
        component_name: 'ModalWhatsappMessager',
        modal_title: this.applicantData.first_name + ' ' + this.applicantData.last_name + ' - מועמד',
        modal_width: 700,
        is_center_modal: true,
        props: {
          applicant: this.applicantData
        }
      })
    },
    openOldApplicantModal () {
      const modalTitle = this.applicantName + this.$utils.getVetted(this.applicantData)
      this.create({
        designator: 'applicant-old-modal',
        component_name: 'ApplicantInfo',
        data: this.applicantData,
        modal_title: modalTitle,
        override_modal_x: 370,
        modal_width: 1500,
        modal_open_action: 'click'
      })
    },
    ...mapMutations('modals', ['create'])
  }
}
</script>
<style>
  .image-style {
    width: 30px;
  }
  .no-overflow-modal {
    overflow: hidden !important;
    height: 70vh;
  }
  .b-r{
    height: 65vh;
    overflow-y: auto;
  }
  .column-width {
    width: 12%;
    flex-basis: inherit;
  }
</style>
